import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import type {
  BillingPlanBudgetInvoice,
  BillingPlanBudgetItem,
  BillingPlanBudgetItemInput,
  BillingPlanBudgetItemInvoiceTypeEnum,
  BillingPlanMonth,
} from "@src/__generated__/graphql";
import type { AppStore } from "@src/stores/AppStore";
import { toApiDate } from "@src/utils/dates";
import { denominate, nominate } from "@src/utils/formatters";
import { gt, lt, required } from "@src/utils/forms/validators";
import type { DateRange } from "@src/utils/types";
import { eachMonthOfInterval } from "date-fns";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

type MonthsMap = Map<string, FieldState<string>>;

export class ProjectBillingPlanSubRow extends FormState<{
  day: FieldState<number | undefined>;
  type: FieldState<BillingPlanBudgetItemInvoiceTypeEnum | undefined>;
  months: FormState<MonthsMap>;
  accounting_center_id: FieldState<string | undefined>;
  accounting_account_id: FieldState<string | undefined>;
}> {
  appStore: AppStore;
  id: string;
  invoices: BillingPlanBudgetInvoice[] = [];

  public requiredIfMonthsArentEmpty = ($: string | number | undefined) => {
    if (!this.areMonthsEmpty) return required($);
    return null;
  };

  constructor(
    appStore: AppStore,
    id: string,
    source?: Partial<BillingPlanBudgetItem>,
  ) {
    super({
      day: new FieldState(source?.day ? source.day : undefined),
      type: new FieldState(source?.invoice_type ?? undefined),
      months: new FormState(new Map<string, FieldState<string>>()),
      accounting_center_id: new FieldState(
        source?.accounting_centre_id ?? undefined,
      ),
      accounting_account_id: new FieldState(
        source?.accounting_account_id ?? undefined,
      ),
    });
    makeObservable(this, {
      id: observable,
      monthCount: computed,
      areMonthsDirty: computed,
      areMonthsEmpty: computed,
      isDirty: computed,
      mutationInput: computed,
      setMonth: action.bound,
      setMonths: action,
      distributeBetweenMonths: action.bound,
      duplicateFirstMonthToRest: action.bound,
      clearRow: action.bound,
    });
    this.id = id;
    this.appStore = appStore;
    this.$.day.validators(gt(0), lt(32), this.requiredIfMonthsArentEmpty);
    this.$.type.validators(this.requiredIfMonthsArentEmpty);

    if (source?.months) {
      this.setMonths(source.months);
      this.invoices = source.months
        .flatMap((month) => month.billingPlanInvoice)
        .filter((invoice) => !!invoice);
    } else {
      this.setMonths([]);
    }
  }

  get monthCount(): number {
    return this.$.months.$.size;
  }

  getFirstMonth(period: DateRange) {
    return this.$.months.$.get(toApiDate(period.start.setDate(1)));
  }

  get areMonthsDirty(): boolean {
    let monthsDirty = false;

    for (const field of this.$.months.$.values()) {
      if (!field.dirty) continue;
      monthsDirty = true;
      break;
    }

    return monthsDirty;
  }

  get areMonthsEmpty(): boolean {
    let empty = true;

    for (const field of this.$.months.$.values()) {
      if (field.$ === "0") continue;
      empty = false;
    }

    return empty;
  }

  get isDirty(): boolean {
    return (
      this.areMonthsDirty ||
      !!this.$.day.dirty ||
      !!this.$.type.dirty ||
      !!this.$.accounting_center_id.dirty ||
      !!this.$.accounting_account_id.dirty
    );
  }

  get mutationInput(): BillingPlanBudgetItemInput | undefined {
    if (
      !this.$.day.$ &&
      !this.$.type.$ &&
      this.areMonthsEmpty &&
      !this.$.accounting_center_id.$ &&
      !this.$.accounting_account_id.$
    )
      return undefined;

    return {
      invoice_type: this.$.type.$,
      day: this.$.day.$,
      billingPlanMonths: Array.from(this.$.months.$.entries()).map(
        ([monthAsString, field]) => ({
          date: monthAsString,
          value: denominate(
            Number(field.$),
            this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
          ),
        }),
      ),
      accounting_centre_id: this.$.accounting_center_id.$,
      accounting_account_id: this.$.accounting_account_id.$,
    };
  }

  setMonth(date: Date, amount: number, map: MonthsMap = this.$.months.$) {
    map.set(toApiDate(date.setDate(1)), new FieldState(amount.toString()));
  }

  setMonths(months: BillingPlanMonth[]) {
    const monthsMap: MonthsMap = new Map();

    for (const { date, value } of months) {
      this.setMonth(
        new Date(date),
        nominate(
          value,
          this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
        ),
        monthsMap,
      );
    }

    this.$.months = new FormState(monthsMap);
  }

  distributeBetweenMonths(budget: number, every: number) {
    const count = this.$.months.$.size;
    const months = Array.from(this.$.months.$.values());
    const valueToDistribute =
      nominate(
        budget,
        this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ) / Math.ceil(count / every);

    for (let i = 0; i < count; i++) {
      const month = months[i];
      if (!month) continue;
      if (i % every !== 0) {
        month.onChange("0");
        continue;
      }
      month.onChange(valueToDistribute.toFixed(2));
    }
  }

  duplicateFirstMonthToRest(period: DateRange) {
    const firstMonth = this.getFirstMonth(period);
    if (!firstMonth) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong, please try again later.`,
      });
      captureEvent({
        message: "FE: Couldn't find first month of row.",
      });
      return;
    }

    for (const month of this.$.months.$.values()) {
      month.onChange(firstMonth.$);
    }
  }

  clearRow(period: DateRange) {
    this.$.type.onChange(undefined);
    this.$.day.onChange(undefined);
    this.$.accounting_center_id.onChange(undefined);
    this.$.accounting_account_id.onChange(undefined);
    this.setMonths(
      eachMonthOfInterval(period).map((month) => ({
        date: toApiDate(month),
        value: 0,
      })),
    );
  }

  getFieldByDate(month: Date) {
    return this.$.months.$.get(toApiDate(month));
  }
}
